import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { captureEvent } from "@/lib/analyticsQueue";
import { useCredits } from "@/contexts/CreditsContext";
import { useCreditCosts } from "@/hooks/useCreditCosts";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/lib/toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { useGeneratedImages } from "@/hooks/useGeneratedImages";
import { generateAdImages } from "@/lib/api/generateAd";
import { improvePrompt } from "@/lib/api/improvePrompt";
import { uploadSingleImageToR2 } from "@/lib/api/uploadImage";
import { useImageUpload } from "@/hooks/useImageUpload";
import { ACCEPTED_IMAGE_TYPES } from "@/config/media";
import { downloadMedia, detectAspectRatio, isUrl, shareMedia } from "@/lib/mediaUtils";
import { useModels } from "@/hooks/useModels";
import { useModelWarning } from "@/hooks/useModelWarning";
import { useImageDelete } from "@/hooks/useImageDelete";
import { useGenerationRestore } from "@/hooks/useGenerationRestore";
import { captureGenerationError } from "@/lib/sentry";
import {
  clearFlowState,
  useFlowPersistence,
} from "@/hooks/useFlowPersistence";
import { FLOW_KEYS } from "@/config/flowKeys";
import { ASPECT_RATIOS, RESOLUTIONS } from "@/components/flowly/AspectRatioPicker/constants";
import { imageToDisplayMedia } from "@/lib/types/media";
import type { DisplayMedia, UploadedReferenceImage } from "@/lib/types/media";
import { useLightboxNavigation } from "@/hooks/useLightboxNavigation";
import { usePersistentPendingBoost } from "@/hooks/usePersistentPendingBoost";
import { ANALYTICS_EVENTS, FLOW_IDS } from "@shared/schemas/analytics";
import { AD_IMAGES_PER_GENERATION, CONTENT_FLAGGED_CODE } from "@shared/schemas/enums";
import {
  STUDIO_PRESETS,
  STUDIO_ADVANCED_PRESET_ID,
  STUDIO_PRESETS_ANCHOR_ID,
  STUDIO_CONFIG_ANCHOR_ID,
} from "./constants";

const AD_CREATOR_MODEL_ID = "nano-banana-2";
const FLOW_STATE_KEY = FLOW_KEYS.PRODUCT_CATALOG;

// --- Hook ---

export function useGenerateProductCatalog() {
  const location = useLocation();
  const { deductCredits, refreshCredits, handleCreditError } = useCredits();
  const { getImageGenerateCost, getImageModelCost, costsLoading } = useCreditCosts();
  const { profile } = useAuth();
  const { t, language, locale } = useLanguage();
  const { normalizeOrWarn } = useImageUpload();
  const { models: allModels, refetchModels } = useModels("image");
  const models = useMemo(
    () => allModels.filter((m) => m.id === AD_CREATOR_MODEL_ID),
    [allModels],
  );
  const [imageModel, setImageModel] = useState<string>(AD_CREATOR_MODEL_ID);
  const modelWarning = useModelWarning(imageModel, models);

  useEffect(() => {
    if (!imageModel && models.length > 0) {
      setImageModel(AD_CREATOR_MODEL_ID);
    }
  }, [models, imageModel]);

  // --- Analytics: tool page view ---
  useEffect(() => {
    captureEvent(ANALYTICS_EVENTS.TOOL_PAGE_VIEW, { flow: FLOW_IDS.PRODUCT_CATALOG });
  }, []);

  // Handle navigation from Gallery with a source image
  useEffect(() => {
    if (location.state?.imagePreview) {
      setFilePreview(location.state.imagePreview);
      window.history.replaceState({}, document.title);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.state?.imagePreview]);

  const selectedModel = models.find((m) => m.id === imageModel);

  const availableAspectRatios = useMemo(() => {
    const supported = selectedModel?.capabilities.supportedAspectRatios;
    if (!supported) return ASPECT_RATIOS;
    return ASPECT_RATIOS.filter((r) => supported.includes(r.id));
  }, [selectedModel]);

  const availableResolutions = useMemo(() => {
    const supported = selectedModel?.capabilities.supportedResolutions;
    if (!supported) return RESOLUTIONS;
    return RESOLUTIONS.filter((r) => supported.includes(r.id));
  }, [selectedModel]);

  const handleImageModelChange = (modelId: string) => {
    setImageModel(modelId);
    const newModel = models.find((m) => m.id === modelId);
    const supported = newModel?.capabilities.supportedAspectRatios;
    if (supported && !supported.includes(aspectRatio)) {
      setAspectRatio(supported[0] ?? "1:1");
    }
    const supportedRes = newModel?.capabilities.supportedResolutions;
    if (supportedRes && !supportedRes.includes(resolution)) {
      setResolution(supportedRes[0] as "1k" | "2k" | "4k");
    }
  };
  const { deleteFailedImages, deleteImageByUrl } = useImageDelete();

  // --- Refs ---
  const fileInputRef = useRef<HTMLInputElement>(null);
  const profileInputRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);

  // --- State ---
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [additionalImages, setAdditionalImages] = useState<UploadedReferenceImage[]>([]);

  const [userPrompt, setUserPrompt] = useState("");
  const [showCustomPrompt, setShowCustomPrompt] = useState(false);

  const handleToggleCustomPrompt = () => {
    setShowCustomPrompt((prev) => !prev);
  };
  const [aspectRatio, setAspectRatio] = useState("1:1");
  const [resolution, setResolution] = useState<"1k" | "2k" | "4k">("2k");
  const [detectedAspectRatio, setDetectedAspectRatio] = useState<string | null>(null);
  const [backgroundColor, setBackgroundColor] = useState<"gray" | "white">("gray");

  // Counter instead of boolean — supports concurrent generations
  const [generatingCount, setGeneratingCount] = useState(0);
  const generating = generatingCount > 0;
  const [selectedMedia, setSelectedMedia] = useState<DisplayMedia | null>(null);

  const [isEnhancingPrompt, setIsEnhancingPrompt] = useState(false);
  // Selected style preset id (or STUDIO_ADVANCED_PRESET_ID to open the
  // advanced panel). null = nothing picked yet → generate button is gated.
  const [presetId, setPresetId] = useState<string | null>(null);
  // Timestamp (ms) of when the most recent generation in *this* session
  // kicked off. Used by the hero to filter the gallery down to just the
  // current session's results — so swapping the source photo doesn't show
  // stale "after" thumbnails from a previous run.
  const [latestGenerationStartedAt, setLatestGenerationStartedAt] = useState<
    number | null
  >(null);

  // --- Gallery ---
  const { images: dbImages, pendingImages: dbPendingImages, pendingCount: dbPendingCount, completingIds: imageCompletingIds, failedImages, loading: imagesLoading } = useGeneratedImages(undefined, "ad");

  // Persistent pending boost — survives page refresh via sessionStorage
  const { pendingCount: pendingImages, increment: incrementPending, decrement: decrementPending } =
    usePersistentPendingBoost({ pipeline: "ad", dbPendingCount, isDbLoading: imagesLoading });

  // Restore prompt & image from pending DB items on page refresh
  const setPromptWithToggle = useCallback((val: string) => {
    setUserPrompt(val);
    if (val) setShowCustomPrompt(true);
  }, []);

  useGenerationRestore({
    pendingItems: dbPendingImages,
    isLoading: imagesLoading,
    currentPrompt: userPrompt,
    setPrompt: setPromptWithToggle,
    currentImage: filePreview,
    setImage: setFilePreview,
  });

  // Derive gallery from completed dbImages (auto-updates via Realtime)
  const galleryImages: DisplayMedia[] = useMemo(() => {
    return dbImages.map(imageToDisplayMedia);
  }, [dbImages]);

  // Navigation for gallery lightbox
  const { currentMediaIndex, canNavigate, goToNextItem, goToPrevItem } =
    useLightboxNavigation(galleryImages, selectedMedia, setSelectedMedia);

  // --- Handlers ---

  const handleFileSelect = async (rawFile: File) => {
    const selectedFile = await normalizeOrWarn(rawFile);
    if (!selectedFile) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(selectedFile.type)) return;
    captureEvent(ANALYTICS_EVENTS.IMAGE_UPLOAD, { flow: FLOW_IDS.PRODUCT_CATALOG });
    setFile(selectedFile);
    // Source photo changed → invalidate the hero's "after" thumbnails.
    setLatestGenerationStartedAt(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setFilePreview(result);

      // Guide the user to the next step (preset selection) once their photo
      // is loaded. rAF lets the preset section mount before scrollIntoView.
      requestAnimationFrame(() => {
        document
          .getElementById(STUDIO_PRESETS_ANCHOR_ID)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });

      const img = new Image();
      img.onload = () => {
        const detected = detectAspectRatio(img.width, img.height);
        setDetectedAspectRatio(detected);
        const supported = selectedModel?.capabilities.supportedAspectRatios;
        if (supported && !supported.includes(detected)) {
          setAspectRatio(supported[0] ?? "1:1");
        } else {
          setAspectRatio(detected);
        }
      };
      img.src = result;
    };
    reader.readAsDataURL(selectedFile);
  };

  const handlePresetSelect = useCallback((id: string) => {
    setPresetId(id);
    captureEvent(ANALYTICS_EVENTS.TEMPLATE_SELECTED, {
      flow: FLOW_IDS.PRODUCT_CATALOG,
      preset_id: id,
    });
    if (id === STUDIO_ADVANCED_PRESET_ID) {
      // "Custom" card — open the advanced panel and let the user write their
      // own prompt. Scroll to the advanced section so the textarea is in view.
      setShowCustomPrompt(true);
      requestAnimationFrame(() => {
        document
          .getElementById(STUDIO_CONFIG_ANCHOR_ID)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } else {
      // Regular style preset — close advanced; the preset's prompt is applied
      // server-side via handleGenerate (we don't push it into userPrompt so the
      // user doesn't see implementation details).
      setShowCustomPrompt(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) handleFileSelect(selectedFile);
  };

  const clearFile = () => {
    setFile(null);
    setFilePreview(null);
    setLatestGenerationStartedAt(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleProfileFileSelect = async (rawFile: File) => {
    const selectedFile = await normalizeOrWarn(rawFile);
    if (!selectedFile) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(selectedFile.type)) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setProfilePreview(result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleProfileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) handleProfileFileSelect(selectedFile);
  };

  const clearProfileFile = () => {
    setProfilePreview(null);
    if (profileInputRef.current) profileInputRef.current.value = "";
  };

  const handleResolutionChange = (newResolution: "1k" | "2k" | "4k") => {
    setResolution(newResolution);
  };

  const handleEnhancePrompt = async () => {
    if (!userPrompt.trim() || isEnhancingPrompt) return;
    captureEvent(ANALYTICS_EVENTS.PROMPT_ENHANCE_CLICK, { flow: FLOW_IDS.PRODUCT_CATALOG });
    setIsEnhancingPrompt(true);
    try {
      const response = await improvePrompt({ prompt: userPrompt, mode: "ad" });
      if (response.success && response.improvedPrompt) {
        setUserPrompt(response.improvedPrompt);
        toast.success(t.generate.promptImproved, {
          description: t.generate.promptImprovedDesc,
        });
      } else if (!response.isRateLimited) {
        toast.error(t.generate.enhanceFailed, {
          description: response.error || t.generate.enhanceError,
        });
      }
    } catch (error) {
      console.error("Error improving prompt:", error);
      captureGenerationError(error, {
        flow: FLOW_IDS.PRODUCT_CATALOG,
        action: "enhance_prompt",
        endpoint: "/api/improve-prompt",
      });
      toast.error(t.common.error, {
        description: t.generate.enhanceError,
      });
    } finally {
      setIsEnhancingPrompt(false);
    }
  };

  const generateAds = async () => {
    if (!file && !filePreview) {
      toast.error(t.generate.uploadRequired, {
        description: t.generate.uploadRequiredDesc,
      });
      return;
    }

    const plan = profile?.subscription_name;
    if (generating && plan !== "pro" && plan !== "creator") {
      toast.info(t.generate.generationInProgress, { description: t.generate.parallelProRequired });
      return;
    }

    const totalCost = AD_IMAGES_PER_GENERATION * getImageGenerateCost(resolution, imageModel);

    if (!deductCredits(totalCost)) return;

    setGeneratingCount((c) => c + 1);
    incrementPending(AD_IMAGES_PER_GENERATION);
    // Mark the boundary so the hero can pick up only this run's results.
    setLatestGenerationStartedAt(Date.now());

    setTimeout(() => {
      galleryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);

    let doneHandled = false;
    let succeeded = false;
    try {
      // Upload product image to R2 (handles both base64 and URL sources)
      const productImageUrl = isUrl(filePreview!)
        ? filePreview!
        : await uploadSingleImageToR2(filePreview!);

      // Upload profile image if present
      let profileImageUrl: string | undefined;
      if (profilePreview) {
        profileImageUrl = isUrl(profilePreview)
          ? profilePreview
          : await uploadSingleImageToR2(profilePreview);
      }

      // Build reference image URLs (product + profile + additional)
      const referenceImageUrls: string[] = [productImageUrl];
      if (profileImageUrl) referenceImageUrls.push(profileImageUrl);
      for (const img of additionalImages) {
        const url = isUrl(img.url) ? img.url : await uploadSingleImageToR2(img.url);
        referenceImageUrls.push(url);
      }

      // Resolve final prompt: a selected style preset takes priority over the
      // manual textarea; "advanced" preset (or no preset) falls back to the
      // user's custom prompt if they wrote one.
      const presetPrompt =
        presetId && presetId !== STUDIO_ADVANCED_PRESET_ID
          ? STUDIO_PRESETS.find((p) => p.id === presetId)?.prompt
          : null;
      const customPrompt =
        showCustomPrompt && userPrompt.trim() ? userPrompt.trim() : null;
      const finalPrompt = presetPrompt ?? customPrompt;

      let deliveredCount = 0;
      await generateAdImages(
        {
          imageUrl: productImageUrl,
          aspectRatio,
          backgroundColor,
          resolution,
          imageCount: AD_IMAGES_PER_GENERATION,
          referenceImageUrls,
          ...(profileImageUrl && { profileImageUrl }),
          ...(finalPrompt && { userPrompt: finalPrompt }),
          modelId: imageModel,
        },
        {
          onImage: () => {
            deliveredCount++;
            decrementPending(1);
          },
          onDone: () => {
            doneHandled = true;
            succeeded = deliveredCount > 0;
            // Clean up boost for images that failed/weren't delivered
            const undelivered = AD_IMAGES_PER_GENERATION - deliveredCount;
            if (undelivered > 0) {
              decrementPending(undelivered);
            }
            toast.success(t.generate.imagesReady, {
              description: t.generate.imagesReadyDesc,
            });
          },
          onError: (error) => {
            refreshCredits();
            const isFlagged = error === CONTENT_FLAGGED_CODE;
            toast.error(
              isFlagged ? t.generate.contentFlagged : t.generate.generationFailed,
              {
                description: isFlagged
                  ? t.generate.contentFlaggedDesc
                  : (error || t.generate.generationFailedWithRefund),
              }
            );
          },
        }
      );
    } catch (error) {
      console.error("Error generating images:", error);
      captureGenerationError(error, {
        flow: FLOW_IDS.PRODUCT_CATALOG,
        action: "generate",
        model: imageModel,
        endpoint: "/api/generate-ad-images",
      });
      refetchModels();
      if (!handleCreditError(error)) {
        refreshCredits();
        const errorMessage = error instanceof Error ? error.message : undefined;
        toast.error(t.generate.generationFailed, {
          description: errorMessage || t.generate.generationFailedWithRefund,
        });
      }
    } finally {
      setGeneratingCount((c) => Math.max(0, c - 1));
      if (!doneHandled) {
        decrementPending(AD_IMAGES_PER_GENERATION);
      }
      await refreshCredits();
      if (succeeded) {
        // Wipe the persisted draft AND reset the per-product inputs to
        // defaults — you don't regenerate from the same product image.
        // Results show in the DB-backed gallery, untouched here.
        // aspect/resolution/background/model are sticky pickers, left as-is.
        clearFlowState(FLOW_STATE_KEY);
        setFile(null);
        setFilePreview(null);
        setProfilePreview(null);
        setAdditionalImages([]);
        setUserPrompt("");
        setShowCustomPrompt(false);
        setPresetId(null);
        setDetectedAspectRatio(null);
      }
    }
  };

  // Auth gate lives inside GenerateButton, so this is just a rename.
  const handleGenerate = generateAds;

  // Persist user's prompt + style choices + source previews so the signup
  // modal (and Google OAuth roundtrip) doesn't wipe their work. The product
  // and profile previews are image keys → their (large) bytes go to
  // IndexedDB instead of localStorage (no QuotaExceeded), restored as the
  // same data-URL strings generate() expects.
  useFlowPersistence({
    key: FLOW_STATE_KEY,
    state: {
      userPrompt,
      showCustomPrompt,
      aspectRatio,
      resolution,
      backgroundColor,
      presetId,
      imageModel,
      filePreview,
      profilePreview,
    },
    imageKeys: ["filePreview", "profilePreview"],
    onRestore: (s) => {
      if (typeof s.userPrompt === "string") setUserPrompt(s.userPrompt);
      if (typeof s.showCustomPrompt === "boolean") setShowCustomPrompt(s.showCustomPrompt);
      if (typeof s.aspectRatio === "string") setAspectRatio(s.aspectRatio);
      if (s.resolution === "1k" || s.resolution === "2k" || s.resolution === "4k") {
        setResolution(s.resolution);
      }
      if (s.backgroundColor === "gray" || s.backgroundColor === "white") {
        setBackgroundColor(s.backgroundColor);
      }
      if (typeof s.presetId === "string" || s.presetId === null) {
        setPresetId(s.presetId as string | null);
      }
      if (typeof s.imageModel === "string") setImageModel(s.imageModel);
      if (typeof s.filePreview === "string" && s.filePreview) setFilePreview(s.filePreview);
      if (typeof s.profilePreview === "string" && s.profilePreview) setProfilePreview(s.profilePreview);
    },
  });

  // --- Lightbox callbacks ---

  const handleDownload = async () => {
    if (!selectedMedia) return;
    await downloadMedia(selectedMedia.url, "todoai-creation", "png");
  };

  const handleShare = () => {
    if (!selectedMedia?.id) return;
    void shareMedia({ id: selectedMedia.id, type: selectedMedia.type });
  };

  const handleRegenerate = () => {
    setFilePreview(selectedMedia?.referenceImageUrls[0] || null);
    setLatestGenerationStartedAt(null);
    const prompt = selectedMedia?.prompt || "";
    setUserPrompt(prompt);
    if (prompt) setShowCustomPrompt(true);
    setSelectedMedia(null);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
  };

  const handleDelete = async () => {
    if (!selectedMedia) return;
    const urlToDelete = selectedMedia.url;
    setSelectedMedia(null);
    await deleteImageByUrl(urlToDelete);
  };

  const handleUseSourceImage = handleRegenerate;

  const handleCloseLightbox = () => setSelectedMedia(null);

  const handleClearFailed = () => {
    if (failedImages.length > 0) {
      deleteFailedImages(failedImages.map((f) => f.id));
    }
  };

  const handleDisabledClick = useCallback(() => {
    // If the user hasn't uploaded yet, guide them to the upload zone. Once
    // uploaded, guide them to the preset grid (the most likely missing step).
    if (!filePreview) {
      document
        .querySelector('[data-scroll-target="upload"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    document
      .getElementById(STUDIO_PRESETS_ANCHOR_ID)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [filePreview]);

  // --- Return ---

  return {
    // Refs
    fileInputRef,
    profileInputRef,
    galleryRef,

    // File upload
    file,
    filePreview,
    profilePreview,
    additionalImages,
    setAdditionalImages,
    handleFileSelect,
    handleInputChange,
    clearFile,
    handleProfileFileSelect,
    handleProfileInputChange,
    clearProfileFile,
    detectedAspectRatio,

    // User prompt
    userPrompt,
    setUserPrompt,
    showCustomPrompt,
    handleToggleCustomPrompt,
    isEnhancingPrompt,
    handleEnhancePrompt,

    // Generation options
    aspectRatio,
    setAspectRatio,
    resolution,
    availableResolutions,
    handleResolutionChange,
    backgroundColor,
    setBackgroundColor,

    // Generation
    generating,
    pendingImages,
    dbPendingImages,
    imageCompletingIds,
    failedImages,
    handleClearFailed,
    handleGenerate,
    getImageCreditCost: (res: string, model: string) => AD_IMAGES_PER_GENERATION * getImageGenerateCost(res, model),
    getImageModelCost,
    costsLoading,

    // Gallery
    galleryImages,
    selectedMedia,
    setSelectedMedia,

    // Lightbox
    handleDownload,
    handleShare,
    handleRegenerate,
    handleUseSourceImage,
    handleDelete,
    handleCloseLightbox,

    // Lightbox navigation
    currentMediaIndex,
    canNavigate,
    goToNextItem,
    goToPrevItem,

    // Disabled click
    handleDisabledClick,

    // Studio preset selection
    presetId,
    handlePresetSelect,
    latestGenerationStartedAt,

    // Models
    models,
    allModels,
    modelWarning,
    imageModel,
    handleImageModelChange,
    availableAspectRatios,

    // Derived
    language,
    locale,
    t,
  };
}
